// Structural fixes served from a scan (fix types heading-outline, landmark,
// clickable, media-pause). Each one acts only where the page itself falls short,
// re-reads the live DOM on every pass, and marks what it touched so reports can
// tell a runtime patch from a fix at source.
//   1.3.1/2.4.6  heading-outline — screen readers hear a gap-free outline
//   1.3.1/2.4.1  landmark        — nav / banner / contentinfo the page lacks
//   2.1.1/4.1.2  clickable       — a click-only <div>/<span> becomes a button
//   2.2.2/1.4.2  media-pause     — a visible pause button on moving video

import { hasLandmark, findLandmarkBlock } from '@nagisot/wcag-rules/landmark-find';

const OUTLINE = 'data-nagisot-outline';
const LIVE = 'data-nagisot-live';

function lang(): string {
  return (document.documentElement.lang || 'he').slice(0, 2).toLowerCase();
}

function shown(el: Element): boolean {
  return el.getClientRects().length > 0 && !el.closest('[hidden], [aria-hidden="true"]');
}

// ── heading outline ─────────────────────────────────────────────────────────
/** Each heading's level as heard by AT becomes its parent heading's + 1, where
 *  the parent is the nearest earlier heading of a lower original level:
 *  h1 h3 h3 h2 → 1 2 2 2, h1 h3 h4 → 1 2 3, and a page whose first heading is
 *  h2 starts at 1. Never raises a level. The visual page is untouched
 *  (aria-level only); a level the site set itself counts as the heading's
 *  level. Idempotent across passes. */
export function normalizeHeadingOutline(): void {
  const hs = Array.from(document.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, [role="heading"][aria-level]'));
  const stack: Array<{ orig: number; eff: number }> = [];
  for (const h of hs) {
    const mine = h.getAttribute(OUTLINE);
    if (!shown(h)) continue;
    const tagLevel = /^H[1-6]$/.test(h.tagName) ? Number(h.tagName[1]) : 0;
    const actual = mine ? Number(mine) : Number(h.getAttribute('aria-level')) || tagLevel;
    if (!actual) continue;
    while (stack.length && stack[stack.length - 1]!.orig >= actual) stack.pop();
    const eff = (stack[stack.length - 1]?.eff ?? 0) + 1;
    stack.push({ orig: actual, eff });
    if (eff !== actual) {
      h.setAttribute('aria-level', String(eff));
      if (!tagLevel && !h.getAttribute('role')) h.setAttribute('role', 'heading');
      h.setAttribute(OUTLINE, String(actual));
    } else if (mine) {
      h.removeAttribute('aria-level');
      h.removeAttribute(OUTLINE);
    }
  }
}

// ── landmarks ───────────────────────────────────────────────────────────────
// The block is found by the same code the scanner's landmarks rule uses, so a
// reported gap is exactly the block that gets the role here.
const NAV_LABEL: Record<string, string> = { he: 'ניווט ראשי', ar: 'التنقل الرئيسي', ru: 'Основная навигация' };

export function ensureLandmark(kind: string): void {
  if (!document.body) return;
  if (kind !== 'nav' && kind !== 'banner' && kind !== 'contentinfo') return;
  if (hasLandmark(document, kind)) return;
  const el = findLandmarkBlock(document, kind);
  if (!el || el.getAttribute('role')) return;
  el.setAttribute('role', kind === 'nav' ? 'navigation' : kind);
  el.setAttribute(LIVE, kind);
  if (kind === 'nav' && !el.getAttribute('aria-label')) el.setAttribute('aria-label', NAV_LABEL[lang()] || 'Main navigation');
}

// ── click-only elements ─────────────────────────────────────────────────────
const NATIVE = 'a[href], button, input, select, textarea, summary, [contenteditable="true"]';

export function makeClickableAccessible(el: Element): void {
  if (!(el instanceof HTMLElement) || el.matches(NATIVE) || el.hasAttribute('data-nagisot-clickable')) return;
  if (!el.hasAttribute('tabindex')) el.tabIndex = 0;
  if (!el.getAttribute('role')) el.setAttribute('role', 'button');
  el.addEventListener('keydown', (e) => {
    if (e.target !== el || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    el.click();
  });
  el.setAttribute('data-nagisot-clickable', '1');
}

// ── moving video ────────────────────────────────────────────────────────────
const PAUSE_TEXT: Record<string, [string, string]> = {
  he: ['השהיית הווידאו', 'הפעלת הווידאו'],
  ar: ['إيقاف الفيديو مؤقتًا', 'تشغيل الفيديو'],
};

export function addMediaPause(el: Element): void {
  if (!(el instanceof HTMLMediaElement) || el.hasAttribute('data-nagisot-pause')) return;
  if (el.hasAttribute('controls')) return; // the native controls already pause it
  const parent = el.parentElement;
  if (!parent) return;
  const [pauseLabel, playLabel] = PAUSE_TEXT[lang()] || ['Pause video', 'Play video'];
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('data-nagisot', 'pause');
  btn.style.cssText = 'position:absolute;top:12px;inset-inline-end:12px;z-index:2147483000;width:40px;height:40px;'
    + 'border-radius:50%;border:2px solid #fff;background:rgba(11,18,32,.78);color:#fff;cursor:pointer;'
    + 'font:700 14px/1 system-ui,sans-serif;display:flex;align-items:center;justify-content:center;padding:0';
  const sync = (): void => {
    const paused = el.paused;
    btn.textContent = paused ? '▶' : '❚❚';
    btn.setAttribute('aria-label', paused ? playLabel : pauseLabel);
    btn.setAttribute('aria-pressed', String(paused));
  };
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (el.paused) void el.play().catch(() => {}); else el.pause();
  });
  el.addEventListener('play', sync);
  el.addEventListener('pause', sync);
  if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
  parent.insertBefore(btn, el.nextSibling);
  el.setAttribute('data-nagisot-pause', '1');
  // Visitors who asked the OS for less motion get it paused from the start.
  try { if (matchMedia('(prefers-reduced-motion: reduce)').matches) el.pause(); } catch { /* noop */ }
  sync();
}
